import api from '@/lib/api';
import type {ExecutionSymbolReadiness} from './forex-api';
export interface MtfReadiness extends Record<string,unknown> {platform_execution?:{required_symbols:string[];account_id:string|null;status:'ready'|'blocked'|'unavailable';symbols:ExecutionSymbolReadiness[];reason_codes:string[]}}
export interface MtfStrategy {
  schema_version: 'PolicyBackedStrategyV3';
  trigger_timeframe: string;
  symbol: string;
  cadence_profile_id: string;
  timeframe_roles: Record<string, string>;
  effective_from_close_time: number;
  market_runtime: {closed_bar_counts: Record<string, number>; market_freshness_seconds: number};
}
export interface CadenceProfile {
  profile_id: string; profile_version: number; profile_hash: string; label: string;
  timeframe_roles: Record<string, string>;
  default_signal_validity_seconds: number; demo_capability: string;
}
export interface CadenceCapability { default_profile_id: string; items: CadenceProfile[]; }
export interface MtfBinding { binding_id: string; binding_hash: string; schema_version: 'StrategyBinding'; binding_version: number; config_version: number; enabled: boolean; strategy_configs: MtfStrategy[]; }
export interface MtfRuntimeState {
  agent_id: string;
  requested_enabled: boolean;
  effective_enabled: boolean;
  mode: 'demo_auto';
  binding_version: number;
  runtime_revision: number;
  runtime_started_at?: number | null;
  runtime_stopped_at?: number | null;
  decision_ready?: boolean;
  execution_ready?: boolean | null;
  decision_blockers?: string[];
  execution_blockers?: string[];
  blocking_reasons: string[];
  full_usage_today: number | null;
  full_usage_status: 'available' | 'unavailable';
  updated_at: number;
  updated_by?: string | null;
}
export interface MtfRun { run_id: string; symbol: string; timeframe?: string; cadence_profile_id?: string; timeframe_roles?: Record<string,string>; status: string; error_code: string | null; display_reason_code?: string | null; created_at: number; strategy_category?: string | null; ai_decision_style?: string | null; template_ref?: {id: string; version: number; content_hash: string} | null; rule_hash?: string | null; }
export interface MtfFullAttempt {
  attempt: number;
  status: 'success' | 'failed' | 'unknown' | 'not_invoked';
  reason_code?: string | null;
  route_id?: string | null;
  model_id?: string | null;
  configured_timeout_ms?: number | null;
  effective_timeout_ms?: number | null;
  total_budget_ms?: number | null;
  remaining_budget_ms?: number | null;
  latency_ms?: number | null;
  reserved_at?: number | null;
  completed_at?: number | null;
  invocation_started?: boolean | null;
  failure_class?: 'output_truncated' | 'schema_invalid';
  output_diagnostics?: {finish_reason?: string; content_present?: boolean; reasoning_present?: boolean; prompt_tokens?: number; completion_tokens?: number};
}
export interface MasterJudgment {
  schema_version: 'MasterJudgmentViewV1';
  status: 'completed' | 'not_called' | 'running' | 'failed';
  action: 'BUY' | 'SELL' | 'HOLD' | null;
  proposal_schema_version: string | null;
  summary: string | null;
  timeframe_reasons: Array<{role: string; timeframe: string | null; reason: string | null}>;
  risk_reasons: Array<{kind: string; reason: string | null}>;
  warnings: string[];
  evidence_refs: string[];
  confidence: string | null;
  reason_code: string | null;
}
export interface MtfDetail {
  run: MtfRun; input_summary: Record<string, {count: number; required_count: number; status: string; reason_codes: string[]}>;
  stages: Array<{stage: string; status: string; reason_code: string | null; details: Record<string, unknown>}>;
  features: Record<string, unknown> | null; fast: Record<string, unknown> | null;
  full: Record<string, unknown> | null; admission: Record<string, unknown> | null;
  admission_checks?: Array<{phase: string; status: string; reason_codes: string[]; evaluated_at: number; assessment_hash: string | null; market_snapshot_hash: string | null}>;
  master_judgment?: MasterJudgment;
  full_attempts?: MtfFullAttempt[];
  fallback_reason_code?: string | null;
  input_hash: string; execution_lineage: Record<string, unknown> | null;
  cadence_profile_id?: string; cadence_profile_hash?: string; timeframe_roles?: Record<string,string>;
  strategy_template?: {template_ref: {id: string; version: number; content_hash: string}; rule_hash: string; category: string; ai_decision_style: string; strategy_key: string} | null;
  strategy_candidate?: {
    status: string; candidate_direction?: string | null; setup_type?: string | null;
    reason_code?: string | null; evidence_refs?: string[]; exact_price_plan?: Record<string, string> | null;
    setup_id?: string | null; eligible_directions?: string[];
    evaluation?: {status?: string; as_of?: number; timeframe?: string; reason_codes?: string[];
      evidence?: Record<string, unknown>; eligible_directions?: string[]; setup_id?: string | null} | null;
  } | null;
  explanation?: {explanation_version: string; stage: string; outcome: string; reason_code: string | null; summary_zh: string; details: {checks?: Array<{code: string; passed: boolean; actual: string; expected: string; timeframe: string | null}>; history_gaps?: Array<{role: string; timeframe: string; missing_ranges: Array<{kind: string; expected_open: number; next_open: number}>}>}; evidence_refs: string[]};
  decision_team?: {mode: 'single' | 'multi'; role_ids: string[]} | null;
  role_results?: Array<{role_id: string; status: string; summary: string | null; assessment: string | null; reason_code: string | null; latency_ms: number | null}>;
  master_orders?: RunMasterOrders;
}
export interface RunMasterOrder {
  order_ref: string; account_number_masked: string; broker_ticket_masked: string;
  symbol: string; side: 'BUY' | 'SELL'; lifecycle_status: 'open' | 'closed';
  initial_filled_lot: string | null; lot: string | null;
  open_price: string | null; current_price: string | null; close_price: string | null;
  stop_loss: string | null; take_profit: string | null;
  pnl: string | null; commission: string | null; swap: string | null;
  net_pnl: string | null; currency: string | null;
  opened_at_utc: number | null; closed_at_utc: number | null;
  time_status: string; sync_status: 'fresh' | 'stale' | 'error' | 'unverified';
  pnl_as_of: number | null; updated_at: number;
}
export interface RunMasterOrders {
  status: 'ready' | 'no_signal' | 'no_trade' | 'pending_projection' | 'unverified' | 'unavailable';
  reason_code: string | null; read_at: number; total: number | null;
  truncated: boolean; items: RunMasterOrder[];
}
const root = (agentId: string) => `/forex/platform-agents/${encodeURIComponent(agentId)}`;
// Same business API prefix as forex-api; api owns base URL, JWT and risk-confirmation flow.
const prefix = '/quantum/business';
export const mtfApi = {
  async binding(id: string): Promise<MtfBinding | null> { return (await api.get(`${prefix}${root(id)}/master-signal-binding`)).data.data; },
  async runtime(id: string): Promise<MtfRuntimeState> { return (await api.get(`${prefix}${root(id)}/master-mtf-runtime`)).data.data; },
  async toggleRuntime(id: string, payload: {enabled: boolean; expected_binding_version: number; expected_runtime_revision: number}): Promise<MtfRuntimeState> {
    const idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `mtf-${Date.now()}`;
    return (await api.patch(`${prefix}${root(id)}/master-mtf-runtime`, payload, {headers: {'Idempotency-Key': idempotencyKey}})).data.data;
  },
  async runs(id: string, status = '', cursor?: string): Promise<{items: MtfRun[]; next_cursor: string | null}> { return (await api.get(`${prefix}${root(id)}/master-mtf-runs`, {params: {status: status || undefined, cursor}})).data.data; },
  async detail(id: string, runId: string): Promise<MtfDetail> { return (await api.get(`${prefix}${root(id)}/master-mtf-runs/${encodeURIComponent(runId)}`)).data.data; },
  async readiness(id: string): Promise<MtfReadiness> { return (await api.get(`${prefix}${root(id)}/master-mtf-readiness`)).data.data; },
  async cadenceProfiles(): Promise<CadenceCapability> { return (await api.get(`${prefix}/forex/master-mtf-cadence-profiles`)).data.data; },
};
